import { BaseService, BaseServiceConstructor } from 'api/services/base.service';
import { AsyncCreatedJob } from 'types/responses/AsyncJob';

type ExportFormat = 'csv' | 'excel' | 'geoJSON' | 'gdb';

export class ExportService extends BaseService {
  constructor({ serviceUrls, token, tenantName }: BaseServiceConstructor) {
    super({ serviceUrls, token, tenantName });
  }

  async exportInventory(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/inventory`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportInspections(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/inspections`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportInspectionsTemplate(): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/template/inspections`,
      { format: 'excel' },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportSamplingPool(
    exportFormat: ExportFormat,
    includeSamplePoolHistory: boolean
  ): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/samplingPool`,
      { format: exportFormat, includeSamplePoolHistory: !!includeSamplePoolHistory },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportSamplingPoolTemplate(): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/template/samplingPool`,
      { format: 'excel' },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportCommunications(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/communications`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportCommunicationsReplacements(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/communicationsReplacements`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportReplacementsMailingList(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/replacementsMailingList`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportCustomerContacts(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/customerContacts`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportReplacementPool(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/replacementPool`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportReplacementPoolV2(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/replacementPool/v2`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportRightOfEntry(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/rightOfEntry`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportInspectionOptimizerOutputs(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/inspectionOptimizerOutputs`,
      { format: exportFormat, modelId: 'current' },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportPredict(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/predict`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async exportPropertyRegistrationCodes(exportFormat: ExportFormat): Promise<AsyncCreatedJob> {
    const { data } = await this.publicHttpClient.post<AsyncCreatedJob>(
      `/export/propertyRegistrationCodes`,
      { format: exportFormat },
      {
        withCredentials: false,
      }
    );

    return data;
  }
}
